"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  getDatabase,
  ref,
  update,
  set,
  onValue,
  get,
  push,
  serverTimestamp,
  increment,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
  uploadBytes,
} from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../Firebase/firestore";
import {
  FaTruck,
  FaPencilAlt,
  FaTimes,
  FaInfoCircle,
  FaEye,
  FaCheckCircle,
  FaBan,
  FaFileImport,
  FaFileExcel,
  FaFolderOpen,
  FaTruckLoading,
  FaRupeeSign,
} from "react-icons/fa";
import InfoIcon from "../infoIcon";
import { useCount } from "../contexts/count-context";
import "./userOrders.css";
import UpdateUnderloading from "./UpdateUnderloading";
import DispatchFiles from "./dispatch";
import BalanceUpdatePage from "./balance_update";

const UserOrders = ({
  adminView = false,
  selectedBrand = "",
  onBalanceUpdate = () => {},
}) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [dispatchRecords, setDispatchRecords] = useState([]);
  const [dispatchMatchMode, setDispatchMatchMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [selectedExcelRowIndexes, setSelectedExcelRowIndexes] = useState(
    new Set(),
  );
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [updatedOrders, setUpdatedOrders] = useState({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [brand, setBrand] = useState(
    adminView ? selectedBrand : localStorage.getItem("supplierBrand"),
  );
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const gifUrl = "/Images/New notification.gif";
  const [tooltip, setTooltip] = useState(null);
  const [stopOrderNotifications, setStopOrderNotifications] = useState([]);
  const [PausedOrders, setPausedOrders] = useState({});
  const [ContinuedOrders, setContinuedOrders] = useState({});
  const [truckCount, setTruckCount] = useState(1);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newDispatchQty, setNewDispatchQty] = useState("");
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [dispatchRate, setDispatchRate] = useState("");
  const [deductionAmount, setDeductionAmount] = useState(0);
  const [showPaymentPOModal, setShowPaymentPOModal] = useState(false);
  const [paymentPOFiles, setPaymentPOFiles] = useState({
    payment: null,
    po: null,
    utrNumber: null,
  });
  const [showLowBalanceWarning, setShowLowBalanceWarning] = useState(false);
  const { updateCount } = useCount();
  const lastPendingOrdersCountRef = useRef(null);
  const [showDelayPopup, setShowDelayPopup] = useState(false);
  const [delayReason, setDelayReason] = useState("");
  const [uploadedExcelFile, setUploadedExcelFile] = useState(null);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [isExcelDragging, setIsExcelDragging] = useState(false);
  const [showUnderloading, setShowUnderloading] = useState(false);
  const [showDispatchFiles, setShowDispatchFiles] = useState(false);
  const [showBalanceUpdate, setShowBalanceUpdate] = useState(false);

  const handleExcelFile = async (file) => {
    if (!file) return;
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
      alert("Please select an Excel file (.xlsx or .xls) only.");
      return;
    }
    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
      alert("Excel file size must not exceed 5 MB.");
      return;
    }
    setIsExcelUploading(true);
    try {
      const storage = getStorage();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `Ultratect_Dispatches/${Date.now()}_${safeFileName}`;
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setUploadedExcelFile({ name: file.name, url: downloadUrl });
    } catch (error) {
      console.error("Excel upload failed:", error);
      alert("Failed to upload Excel file. Please try again.");
    } finally {
      setIsExcelUploading(false);
    }
  };

  const handleExcelInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleExcelFile(file);
    e.target.value = "";
  };

  const handleExcelDrop = (e) => {
    e.preventDefault();
    setIsExcelDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleExcelFile(file);
  };

  useEffect(() => {
    if (adminView) setBrand(selectedBrand);
  }, [adminView, selectedBrand]);

  useEffect(() => {
    if (adminView && !selectedBrand) {
      const storedBrand = localStorage.getItem("adminSelectedBrand");
      if (storedBrand) setBrand(storedBrand);
    }
  }, [adminView, selectedBrand]);

  useEffect(() => {
    const pendingOrdersCount = orders.filter(
      (order) => order.status === "Pending",
    ).length;
    if (lastPendingOrdersCountRef.current !== pendingOrdersCount) {
      lastPendingOrdersCountRef.current = pendingOrdersCount;
      updateCount("userOrders", pendingOrdersCount);
    }
  }, [orders, updateCount]);

  const fetchStopOrderNotifications = useCallback(async () => {
    try {
      const db = getDatabase();
      const notificationsRef = ref(db, "supplier_notifications/stop_orders");
      onValue(notificationsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const notificationsList = [];
          const PausedOrdersMap = {};
          const ContinuedOrdersMap = {};
          Object.keys(data).forEach((userId) => {
            Object.keys(data[userId]).forEach((notificationId) => {
              const notification = data[userId][notificationId];
              if (!brand || notification.brand === brand) {
                notificationsList.push({
                  id: notificationId,
                  userId,
                  ...notification,
                });
                if (
                  notification.title === "Order Paused" ||
                  notification.status === "Paused"
                ) {
                  PausedOrdersMap[notification.orderId] = {
                    timestamp: notification.timestamp,
                    status: "Paused",
                  };
                } else if (
                  notification.title === "Order Continued" ||
                  notification.status === "Continued"
                ) {
                  ContinuedOrdersMap[notification.orderId] = {
                    timestamp: notification.timestamp,
                    status: "Continued",
                  };
                }
              }
            });
          });
          setStopOrderNotifications(notificationsList);
          setPausedOrders(PausedOrdersMap);
          setContinuedOrders(ContinuedOrdersMap);
        }
      });
    } catch (error) {
      // Error fetching stop order notifications
    }
  }, [brand]);

  useEffect(() => {
    if (brand) fetchStopOrderNotifications();
  }, [brand, fetchStopOrderNotifications]);

  const getIndianTimestamp = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().replace("T", " ").replace("Z", "");
  };

  const sendNotification = async (userId, orderId, title, message) => {
    const notificationId = `notification_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const notificationRef = ref(
      db,
      `user_notifications/Order Dispatch/${userId}/${notificationId}`,
    );
    const newNotification = {
      title,
      message,
      orderId,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    };
    try {
      await set(notificationRef, newNotification);
    } catch (error) {
      // Error sending notification
    }
  };

  const sendLowBalanceNotification = async (
    userId,
    orderId,
    currentBalance,
    deductionAmount,
    brand,
    grade,
    siteAddress,
  ) => {
    const notificationId = `notification_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const notificationRef = ref(
      db,
      `user_notifications/Low Balance/${userId}/${notificationId}`,
    );
    const newNotification = {
      title: "Low Balance Alert",
      message: `Your current balance of ₹${currentBalance} is insufficient to dispatch ${brand} (${grade}) to ${siteAddress}.Please visit the Order Status page and make a payment of ₹${deductionAmount} to proceed with your order.`,
      orderId,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    };
    try {
      await set(notificationRef, newNotification);
    } catch (error) {
      // Error sending low balance notification
    }
  };

  const logTransaction = async (
    userUid,
    type,
    amount,
    order,
    extraData = {},
  ) => {
    if (!userUid || !amount) return;
    const transId = `trans_${Date.now()}`;
    const transactionData = {
      type,
      amount: Number(amount),
      brand: order.brand,
      codeID: order.code,
      orderId: order.orderId,
      description:
        extraData.description ||
        (type === "credit" ? "Payment Confirmed" : "Dispatch Done"),
      timestamp: getIndianTimestamp(),
      balanceAfter: null,
      ...extraData,
    };
    try {
      const updates = {};
      const transPath = `transactions/${userUid}/${transId}`;
      updates[transPath] = transactionData;
      await update(ref(db), updates);
      console.log(`✅ ${type.toUpperCase()} transaction logged successfully`);
    } catch (error) {
      console.error("Failed to log transaction:", error);
    }
  };

  const isOrderPaused = (orderId) => {
    if (!PausedOrders[orderId]) return false;
    if (ContinuedOrders[orderId]) {
      return (
        new Date(PausedOrders[orderId].timestamp) >
        new Date(ContinuedOrders[orderId].timestamp)
      );
    }
    const pauseTime = new Date(PausedOrders[orderId].timestamp);
    const now = new Date();
    const hoursDiff = (now - pauseTime) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  const isOrderContinued = (orderId) => {
    if (!ContinuedOrders[orderId]) return false;
    if (PausedOrders[orderId]) {
      if (
        new Date(ContinuedOrders[orderId].timestamp) <
        new Date(PausedOrders[orderId].timestamp)
      ) {
        return false;
      }
    }
    const continueTime = new Date(ContinuedOrders[orderId].timestamp);
    const now = new Date();
    const hoursDiff = (now - continueTime) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  const generateMultipleTruckAndDriverInfo = (count) => {
    let result = "";
    for (let i = 1; i <= count; i++) {
      const truckNo = Math.floor(Math.random() * 20) + 1;
      const driverNo = Math.floor(Math.random() * 900) + 100;
      const quantity = Math.floor(Math.random() * 20) + 1;
      result += `Truck No:${truckNo}, Driver No.:${driverNo}, Qty:${quantity}`;
      if (i < count) result += " | ";
    }
    return result;
  };

  const handleInputChange = (orderId, field, value) => {
    const updatedOrdersCopy = { ...updatedOrders };
    if (!updatedOrdersCopy[orderId]) updatedOrdersCopy[orderId] = {};
    updatedOrdersCopy[orderId][field] = value;
    if (field === "rate") {
      setDispatchRate(value);
      if (newDispatchQty && value !== "") {
        const deductionQty = getDeductionQuantityInBags(
          newDispatchQty,
          currentOrder?.quantityType,
        );
        const calculatedDeduction = Math.round(
          Number.parseFloat(value) * deductionQty,
        );
        setDeductionAmount(calculatedDeduction);
      } else {
        setDeductionAmount(0);
      }
    }
    if (field === "status" && value === "Dispatch") {
      if (
        !updatedOrdersCopy[orderId].shippingDetail ||
        updatedOrdersCopy[orderId].shippingDetail === ""
      ) {
        updatedOrdersCopy[orderId].shippingDetail =
          generateMultipleTruckAndDriverInfo(truckCount);
      }
    }
    if (field === "truckCount") {
      updatedOrdersCopy[orderId].shippingDetail =
        generateMultipleTruckAndDriverInfo(Number.parseInt(value));
    }
    setUpdatedOrders(updatedOrdersCopy);
  };

  const fetchOrders = useCallback(
    async (selectedBrand) => {
      try {
        const ordersRef = ref(getDatabase(), "orders");
        onValue(
          ordersRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              const sitesRef = ref(getDatabase(), "new-code");
              onValue(
                sitesRef,
                (sitesSnapshot) => {
                  if (sitesSnapshot.exists()) {
                    const sitesData = sitesSnapshot.val();
                    const filteredOrders = [];
                    const companySet = new Set();
                    const today = new Date().toISOString().split("T")[0];
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    const orderMap = new Map();
                    Object.keys(data).forEach((uid) => {
                      Object.keys(data[uid]).forEach((orderId) => {
                        const orderKey = `${uid}-${orderId}`;
                        const order = data[uid][orderId];
                        const isCementOrder =
                          !order.category ||
                          order.category === "Cement" ||
                          order.category === "Bulker Cement";
                        let brandMatch = false;
                        if (adminView && !selectedBrand) {
                          brandMatch = true;
                        } else {
                          brandMatch = order.brand === selectedBrand;
                        }
                        const getLastTransactionTimestamp = (transactions) => {
                          if (!transactions) return null;
                          const txnArray = Object.values(transactions);
                          txnArray.sort(
                            (a, b) =>
                              new Date(b.timestamp) - new Date(a.timestamp),
                          );
                          return txnArray.length > 0
                            ? txnArray[0].timestamp
                            : null;
                        };
                        if (isCementOrder && brandMatch) {
                          const isCompletedOld =
                            order.completed &&
                            getLastTransactionTimestamp(order.transactions) &&
                            new Date(
                              getLastTransactionTimestamp(order.transactions),
                            ) < oneWeekAgo;
                          if (!isCompletedOld) {
                            let siteBalance = 0;
                            let foundBalanceInDatabase = false;
                            if (
                              order.code &&
                              sitesData &&
                              sitesData[uid] &&
                              sitesData[uid].codes
                            ) {
                              const userCodes = sitesData[uid].codes;
                              Object.keys(userCodes).forEach((codeKey) => {
                                const codeData = userCodes[codeKey];
                                if (codeData.codeID === order.code) {
                                  siteBalance = codeData.balance || 0;
                                  foundBalanceInDatabase = true;
                                }
                              });
                            } else if (
                              order.siteAddress &&
                              sitesData &&
                              sitesData[uid] &&
                              sitesData[uid].brands &&
                              sitesData[uid].brands[order.brand]
                            ) {
                              const userSites =
                                sitesData[uid].brands[order.brand].sites;
                              if (userSites) {
                                Object.keys(userSites).forEach((siteId) => {
                                  const site = userSites[siteId];
                                  if (
                                    site.address === order.siteAddress.address
                                  ) {
                                    siteBalance = site.balance || 0;
                                    foundBalanceInDatabase = true;
                                  }
                                });
                              }
                            }
                            const quantity =
                              order.quantityData?.total || order.quantity || 0;
                            const quantityType = order.quantityType || "bags";
                            let totalDispatched = 0;
                            let totalPending = quantity;
                            let lastDispatchTransaction = null;
                            if (order.dispatchTransactions) {
                              const transactions = Object.values(
                                order.dispatchTransactions,
                              );
                              transactions.sort(
                                (a, b) =>
                                  new Date(b.timestamp) - new Date(a.timestamp),
                              );
                              if (transactions.length > 0) {
                                lastDispatchTransaction = transactions[0];
                                totalDispatched = transactions.reduce(
                                  (sum, transaction) =>
                                    sum +
                                    (Number.parseInt(transaction.dispatch) ||
                                      0),
                                  0,
                                );
                                totalPending = lastDispatchTransaction.pending
                                  ? Number(lastDispatchTransaction.pending)
                                  : quantity - totalDispatched;
                                if (totalPending < 0) totalPending = 0;
                              }
                            }
                            const orderWithBalance = {
                              orderId,
                              userId: uid,
                              ...order,
                              siteAddress: order.siteAddress
                                ? {
                                    ...order.siteAddress,
                                    transportation_type:
                                      order.siteAddress.transportation_type ||
                                      null,
                                  }
                                : null,
                              quantity,
                              quantityType,
                              isToday:
                                order.timestamp &&
                                order.timestamp.startsWith(today),
                              currentBalance: siteBalance,
                              totalDispatched,
                              totalPending,
                              lastDispatchTransaction,
                              payment_status: order.payment_status || "pending",
                            };
                            filteredOrders.push(orderWithBalance);
                            orderMap.set(orderKey, orderWithBalance);
                            companySet.add(order.companyName);
                          }
                        }
                      });
                    });
                    filteredOrders.sort(
                      (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
                    );
                    setOrders(filteredOrders);
                    setCompanies(Array.from(companySet));
                    setIsLoading(false);
                  }
                },
                { onlyOnce: false },
              );
            } else {
              setError("No orders found for this brand.");
              setIsLoading(false);
            }
          },
          (error) => {
            setError("Error fetching orders: " + error.message);
            setIsLoading(false);
          },
        );
      } catch (err) {
        setError("Error fetching orders: " + err.message);
        setIsLoading(false);
      }
    },
    [adminView],
  );

  useEffect(() => {
    if (adminView) {
      fetchOrders(brand);
    } else {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          if (!brand) {
            setError("Brand not selected. Please log in again.");
            setIsLoading(false);
            return;
          }
          fetchOrders(brand);
        } else {
          setError("User not authenticated. Please log in again.");
          setIsLoading(false);
        }
      });
    }
  }, [brand, adminView, fetchOrders]);

  /* ---------- MATCH / APPLY HELPERS ---------- */

  const handleDispatchDataReady = (records) => {
    setDispatchRecords(Array.isArray(records) ? records : []);
    setDispatchMatchMode(true);
    setSelectedOrderIds(new Set());
    setSelectedExcelRowIndexes(new Set());
    setSelectedCompany("");
    setSelectedStatus("");
    setSearchQuery("");
  };

  const normalizeMatchValue = (value) =>
    String(value ?? "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ");

  const getOrderAddressForMatch = (order) => {
    const address = order.siteAddress;
    if (address && typeof address === "object") {
      return [address.address, address.state, address.pincode]
        .filter(Boolean)
        .join(" ");
    }
    return address || "";
  };

  const destinationMatches = (archiveDestination, orderAddress) => {
    const archiveText = normalizeMatchValue(archiveDestination);
    const orderText = normalizeMatchValue(orderAddress);
    if (!archiveText) return true;
    if (!orderText) return false;
    if (orderText.includes(archiveText) || archiveText.includes(orderText)) {
      return true;
    }
    const meaningfulTokens = (text) =>
      text
        .split(" ")
        .filter((token) => token.length >= 4 && !/^\d+$/.test(token));
    const archiveTokens = meaningfulTokens(archiveText);
    const orderTokens = new Set(meaningfulTokens(orderText));
    return archiveTokens.some((token) => orderTokens.has(token));
  };

  const orderMatchesDispatchRecord = (order, record) => {
    const orderCode = normalizeMatchValue(order.code);
    const recordCode = normalizeMatchValue(record.customerCode);
    const orderCompany = normalizeMatchValue(order.companyName);
    const recordCompany = normalizeMatchValue(record.companyName);
    const orderGrade = normalizeMatchValue(order.grade);
    const recordGrade = normalizeMatchValue(record.grade);
    if (!orderCode || orderCode !== recordCode) return false;
    if (!orderCompany || orderCompany !== recordCompany) return false;
    if (!orderGrade || orderGrade !== recordGrade) return false;
    return destinationMatches(
      record.shippingAddress,
      getOrderAddressForMatch(order),
    );
  };

  const getOrdersForDisplay = () => {
    if (!dispatchMatchMode) return orders;
    return orders.filter((order) =>
      dispatchRecords.some((record) =>
        orderMatchesDispatchRecord(order, record),
      ),
    );
  };

  // Excel rows that match at least one order
  const getMatchedExcelRows = () => {
    if (!dispatchMatchMode) return [];
    return dispatchRecords
      .map((record, index) => ({ record, index }))
      .filter(({ record }) =>
        orders.some((order) => orderMatchesDispatchRecord(order, record)),
      );
  };

  // Merge selected Excel rows → one payload (sum qty, join trucks)
  const buildDispatchPayloadFromSelectedExcel = () => {
    const selected = Array.from(selectedExcelRowIndexes)
      .map((i) => dispatchRecords[i])
      .filter(Boolean);
    if (selected.length === 0) return null;

    const truckNos = new Set();
    let totalQty = 0;
    let shippingAddress = "";

    selected.forEach((r) => {
      totalQty += Number(r.deliveredQty || r.dispatchQty) || 0;
      if (r.truckNo) truckNos.add(String(r.truckNo).trim());
      if (!shippingAddress && r.shippingAddress) {
        shippingAddress = r.shippingAddress;
      }
    });

    return {
      deliveredQty: totalQty,
      truckNo: Array.from(truckNos).filter(Boolean).join(" | "),
      shippingAddress,
      customerCode: selected[0].customerCode,
      companyName: selected[0].companyName,
      grade: selected[0].grade,
    };
  };

  const applySelectedExcelToSelectedOrders = () => {
    if (selectedExcelRowIndexes.size === 0) {
      alert("Select at least one Excel row.");
      return;
    }
    if (selectedOrderIds.size === 0) {
      alert("Select at least one order row to apply dispatch to.");
      return;
    }

    const payload = buildDispatchPayloadFromSelectedExcel();
    if (!payload) return;

    selectedOrderIds.forEach((orderId) => {
      const order = orders.find((o) => o.orderId === orderId);
      if (!order) return;

      if (!orderMatchesDispatchRecord(order, payload)) {
        console.warn("Order does not match selected Excel rows:", orderId);
        return;
      }

      handleDispatchAutoUpdate(order, {
        deliveredQty: payload.deliveredQty,
        truckNo: payload.truckNo,
        shippingAddress: payload.shippingAddress,
      });
    });
  };

  const applyFilters = (allOrders, company, status, search) => {
    let result = [...allOrders];
    if (search && search.trim() !== "") {
      const searchTerm = search.toLowerCase().trim();
      result = result.filter((order) => {
        const fullAddress = order.siteAddress
          ? `${order.siteAddress.address || ""} ${order.siteAddress.state || ""} ${order.siteAddress.pincode || ""}`
              .toLowerCase()
              .replace(/,/g, "")
              .replace(/\s+/g, " ")
          : "";
        const searchableFields = [
          order.companyName?.toLowerCase() || "",
          order.brand?.toLowerCase() || "",
          order.grade?.toLowerCase() || "",
          order.orderId?.toLowerCase() || "",
          order.userId?.toLowerCase() || "",
          order.category?.toLowerCase() || "",
          order.status?.toLowerCase() || "",
          order.code?.toLowerCase() || "",
          fullAddress,
        ];
        return searchableFields.some((field) =>
          field.includes(searchTerm.replace(/,/g, "").replace(/\s+/g, " ")),
        );
      });
    }
    if (company) {
      result = result.filter((order) => order.companyName === company);
    }
    if (status) {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === "Paused") {
        result = result.filter((order) => isOrderPaused(order.orderId));
      } else if (lowerStatus === "Continued") {
        result = result.filter((order) => isOrderContinued(order.orderId));
      } else if (lowerStatus === "completed") {
        result = result.filter((order) => order.completed === true);
      } else {
        result = result.filter(
          (order) => order.status?.toLowerCase() === lowerStatus,
        );
      }
    }
    setFilteredOrders(result);
  };

  useEffect(() => {
    applyFilters(
      getOrdersForDisplay(),
      selectedCompany,
      selectedStatus,
      searchQuery,
    );
  }, [
    orders,
    dispatchRecords,
    dispatchMatchMode,
    selectedCompany,
    selectedStatus,
    searchQuery,
  ]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleCompanyChange = (e) => setSelectedCompany(e.target.value);
  const handleStatusChange = (e) => setSelectedStatus(e.target.value);

  const handleTruckCountChange = (e) => {
    const count = Number.parseInt(e.target.value, 10);
    setTruckCount(count);
    if (currentOrder) {
      handleInputChange(currentOrder.orderId, "truckCount", count);
      handleInputChange(
        currentOrder.orderId,
        "shippingDetail",
        generateMultipleTruckAndDriverInfo(count),
      );
    }
  };

  const openShippingModal = (order) => {
    setCurrentOrder(order);
    if (!updatedOrders[order.orderId]) {
      setUpdatedOrders((prev) => ({
        ...prev,
        [order.orderId]: {
          shippingDetail: order.shippingDetail || "",
          status: order.status || "Pending",
        },
      }));
    }
    setShowShippingModal(true);
  };

  const closeShippingModal = () => {
    setShowShippingModal(false);
    setCurrentOrder(null);
    setNewDispatchQty("");
  };

  const normalizeQuantityType = (value) => {
    const type = (value || "").toString().trim().toLowerCase();
    if (type === "mt" || type === "bulk") return "mt";
    return "bags";
  };

  const getDispatchQuantityInOrderUnit = (quantity, sourceType, orderType) => {
    const qty = Number(quantity) || 0;
    const source = normalizeQuantityType(sourceType);
    const target = normalizeQuantityType(orderType);
    if (source === target) return qty;
    if (source === "mt" && target === "bags") {
      return Math.round(qty * 20 * 100) / 100;
    }
    if (source === "bags" && target === "mt") {
      return Math.round((qty / 20) * 100) / 100;
    }
    return qty;
  };

  const getDeductionQuantityInBags = (quantity, quantityType) => {
    const qty = Number(quantity) || 0;
    return normalizeQuantityType(quantityType) === "mt"
      ? Math.round(qty * 20 * 100) / 100
      : qty;
  };

  const handleDispatchAutoUpdate = (matchedOrder, matchedRow) => {
    if (!matchedOrder || !matchedRow) return;

    const excelQuantity = Number(matchedRow.deliveredQty) || 0;
    const orderQuantityType = normalizeQuantityType(matchedOrder.quantityType);

    let normalizedDispatchQty = excelQuantity;
    if (orderQuantityType === "bags") {
      normalizedDispatchQty = Math.round(excelQuantity * 20 * 100) / 100;
    }
    if (orderQuantityType === "mt") {
      normalizedDispatchQty = excelQuantity;
    }

    setCurrentOrder({ ...matchedOrder });
    setNewDispatchQty(String(normalizedDispatchQty));
    setDispatchRate(
      matchedOrder.rate !== undefined && matchedOrder.rate !== null
        ? String(matchedOrder.rate)
        : "",
    );
    setUpdatedOrders((prev) => ({
      ...prev,
      [matchedOrder.orderId]: {
        ...(prev[matchedOrder.orderId] || {}),
        status: "Dispatch",
        shippingDetail:
          matchedRow.truckNo !== undefined &&
          matchedRow.truckNo !== null &&
          matchedRow.truckNo !== ""
            ? `Truck No: ${matchedRow.truckNo}`
            : matchedOrder.shippingDetail || "",
      },
    }));
    setShowUpdateModal(true);
  };

  const openUpdateModal = (order) => {
    setCurrentOrder(order);
    setNewDispatchQty("");
    if (!updatedOrders[order.orderId]) {
      setUpdatedOrders((prev) => ({
        ...prev,
        [order.orderId]: {
          shippingDetail: order.shippingDetail || "",
          status: order.status || "Pending",
          rate: order.rate || "",
        },
      }));
    }
    if (order.rate) {
      setDispatchRate(order.rate.toString());
      if (newDispatchQty) {
        const calculatedDeduction =
          Number.parseFloat(order.rate) * Number.parseFloat(newDispatchQty);
        setDeductionAmount(calculatedDeduction);
      }
    } else {
      setDispatchRate("");
      setDeductionAmount(0);
    }
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setCurrentOrder(null);
    setNewDispatchQty("");
  };

  const openTransactionsModal = (order) => {
    setCurrentOrder(order);
    setShowTransactionsModal(true);
  };

  const closeTransactionsModal = () => {
    setShowTransactionsModal(false);
    setCurrentOrder(null);
  };

  const resolveFirebaseFileUrl = async (fileValue) => {
    if (!fileValue) return null;
    if (fileValue.startsWith("http")) return fileValue;
    const storage = getStorage();
    const fileRef = storageRef(storage, fileValue);
    return await getDownloadURL(fileRef);
  };

  const openPaymentPOModal = async (order) => {
    setCurrentOrder(order);
    try {
      const db = getDatabase();
      const orderRef = ref(db, `orders/${order.userId}/${order.orderId}`);
      const snapshot = await get(orderRef);
      if (!snapshot.exists()) return;
      const orderData = snapshot.val();
      const paymentUrl = await resolveFirebaseFileUrl(orderData.paymentFileUrl);
      const poUrl = await resolveFirebaseFileUrl(orderData.poFileUrl);
      setPaymentPOFiles({
        payment: paymentUrl,
        po: poUrl,
        utrNumber: orderData.utrNumber || null,
      });
      setShowPaymentPOModal(true);
    } catch (error) {
      console.error("Error fetching payment/PO files:", error);
    }
  };

  const closePaymentPOModal = () => {
    setShowPaymentPOModal(false);
    setCurrentOrder(null);
    setPaymentPOFiles({ payment: null, po: null, utrNumber: null });
  };

  useEffect(() => {
    if (dispatchRate && newDispatchQty) {
      const deductionQty = getDeductionQuantityInBags(
        newDispatchQty,
        currentOrder?.quantityType,
      );
      const calculatedDeduction = Math.round(
        Number.parseFloat(dispatchRate) * deductionQty,
      );
      setDeductionAmount(calculatedDeduction);
    } else {
      setDeductionAmount(0);
    }
  }, [dispatchRate, newDispatchQty, currentOrder]);

  const confirmPayment = async (order) => {
    if (order.payment_status === "confirmed") return;
    try {
      const paymentAmount = Number(order.paymentAmount) || 0;
      await update(ref(db, `orders/${order.userId}/${order.orderId}`), {
        payment_status: "confirmed",
        statusUpdatedAt: getIndianTimestamp(),
      });
      await logTransaction(order.userId, "credit", paymentAmount, order, {
        paymentMethod: "utr",
        utrNumber: order.utrNumber || "",
        paymentFileUrl: order.paymentFileUrl || "",
        description: `Payment confirmed via UTR: ${order.utrNumber || "N/A"}`,
      });
      await sendNotification(
        order.userId,
        order.orderId,
        "Payment Confirmed",
        `Your payment of ₹${paymentAmount} has been confirmed. Dispatch can now proceed.`,
      );
      alert("Payment confirmed and credit logged successfully!");
    } catch (e) {
      console.error("Payment confirm failed:", e);
      alert("Failed to confirm payment");
    }
  };

  const rejectPayment = async (order) => {
    if (order.payment_status === "rejected") return;
    try {
      await update(ref(db, `orders/${order.userId}/${order.orderId}`), {
        payment_status: "rejected",
        statusUpdatedAt: getIndianTimestamp(),
      });
      await sendNotification(
        order.userId,
        order.orderId,
        "Payment Rejected",
        `Your payment proof was rejected. Please upload a valid proof or contact support.`,
      );
    } catch (e) {
      console.error("Payment reject failed:", e);
    }
  };

  const handleSave = async (order) => {
    try {
      const saveBtn = document.querySelector(".save-btn");
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = "Saving...";
      }
      const previousOrder = orders.find((o) => o.orderId === order.orderId);
      const { shippingDetail, ...updatedData } =
        updatedOrders[order.orderId] || {};
      const hasStatusChange =
        updatedData?.status && updatedData.status !== previousOrder.status;
      const hasOtherChanges =
        updatedData &&
        Object.keys(updatedData).some(
          (key) => key !== "status" && updatedData[key] !== previousOrder[key],
        );
      const hasNewDispatch = newDispatchQty && newDispatchQty.trim() !== "";
      const isUnderloadingUpdate = updatedData?.status === "Underloading";

      if (!isUnderloadingUpdate && hasNewDispatch) {
        if (
          !newDispatchQty?.trim() ||
          /^0+$/.test(newDispatchQty.trim()) ||
          !updatedOrders[order.orderId]?.shippingDetail?.trim()
        ) {
          alert("Order Placed successfully!");
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = "Save Changes";
          }
          return;
        }
      }

      if (!hasStatusChange && !hasOtherChanges && !hasNewDispatch) {
        setEditingOrderId(null);
        closeShippingModal();
        closeUpdateModal();
        return;
      }

      const shouldUpdateDate =
        updatedData?.status === "Dispatch" || hasNewDispatch;
      const dispatchDate = shouldUpdateDate
        ? new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
        : previousOrder.dispatchDate;

      let currentBalance = 0;
      const existingBalance = previousOrder.existingBalance || 0;
      const dbInstance = getDatabase();

      await new Promise((resolve) => {
        onValue(
          ref(dbInstance, `new-code/${order.userId}/codes`),
          (snapshot) => {
            if (snapshot.exists()) {
              const codesData = snapshot.val();
              let balanceFound = false;
              Object.keys(codesData).forEach((codeKey) => {
                const codeEntry = codesData[codeKey];
                if (
                  codeEntry.codeID?.trim().toLowerCase() ===
                  order.code?.trim().toLowerCase()
                ) {
                  currentBalance = Number(codeEntry.balance) || 0;
                  balanceFound = true;
                }
              });
              if (!balanceFound && hasNewDispatch) {
                alert("No balance found for the given code.");
              }
              currentOrder.currentBalance = currentBalance;
              resolve(true);
            } else {
              if (hasNewDispatch) {
                alert("No code data found for this user.");
              }
              resolve(false);
            }
          },
          { onlyOnce: true },
        );
      });

      if (updatedData?.currentBalance !== undefined) {
        currentBalance = Number(updatedData.currentBalance);
      }

      let calculatedDeduction = 0;
      if (hasNewDispatch) {
        const dispatchQty = Number(newDispatchQty);
        const deductionQty = getDeductionQuantityInBags(
          dispatchQty,
          order.quantityType,
        );
        const currentRate =
          updatedOrders[order.orderId]?.rate || dispatchRate || order.rate;
        calculatedDeduction = Math.round(
          Number.parseFloat(currentRate) * deductionQty,
        );
        if (calculatedDeduction > 0 && calculatedDeduction > currentBalance) {
          setShowLowBalanceWarning(true);
          await sendLowBalanceNotification(
            order.userId,
            order.orderId,
            currentBalance,
            calculatedDeduction,
            order.brand,
            order.grade,
            order.siteAddress?.address || order.siteAddress || "",
          );
          setError(
            `Insufficient balance. Required: ₹${calculatedDeduction.toLocaleString()}, Available: ₹${currentBalance.toLocaleString()}`,
          );
          return;
        }
      }

      setDeductionAmount(calculatedDeduction);

      if (hasNewDispatch && calculatedDeduction > 0) {
        const dispatchQty = Number(newDispatchQty);
        const currentRate =
          updatedOrders[order.orderId]?.rate || dispatchRate || order.rate;
        const transactionId = `trans_${Date.now()}`;
        await logTransaction(
          order.userId,
          "debit",
          calculatedDeduction,
          order,
          {
            description: `Dispatch of ${dispatchQty} ${order.quantityType || "bags"}`,
            dispatchQty,
            dispatchIds: [transactionId],
            rate: currentRate,
          },
        );
      }

      if (hasNewDispatch && calculatedDeduction > 0) {
        currentBalance = Math.max(0, currentBalance - calculatedDeduction);
      }

      const finalUpdateData = shouldUpdateDate
        ? { ...updatedData }
        : { ...updatedData };

      if (order.code && hasNewDispatch) {
        const userCodesRef = ref(dbInstance, `new-code/${order.userId}/codes`);
        await new Promise((resolve) => {
          onValue(
            userCodesRef,
            (snapshot) => {
              if (snapshot.exists()) {
                const codesData = snapshot.val();
                Object.keys(codesData).forEach((codeKey) => {
                  const codeData = codesData[codeKey];
                  if (codeData.codeID === order.code) {
                    const balanceRef = ref(
                      dbInstance,
                      `new-code/${order.userId}/codes/${codeKey}/balance`,
                    );
                    set(balanceRef, currentBalance);
                  }
                });
                resolve(true);
              } else {
                resolve(false);
              }
            },
            { onlyOnce: true },
          );
        });
      }

      if (updatedOrders[currentOrder.orderId]?.status === "Delayed") {
        if (!delayReason.trim()) {
          alert("Please enter delay reason");
          return;
        }
        const delayRef = ref(
          db,
          `orders/${currentOrder.userId}/${currentOrder.orderId}/delay`,
        );
        await set(delayRef, delayReason);
        const notificationRef = push(
          ref(db, `user_notifications/Order Delay/${currentOrder.userId}`),
        );
        await set(notificationRef, {
          gifUrl: "/Images/New notification.gif",
          message: `Order to "${currentOrder.siteAddress?.address || currentOrder.siteAddress}" delayed due to "${delayReason}"`,
          orderId: currentOrder.orderId,
          seen: false,
          timestamp: getIndianTimestamp(),
          title: "Order Delayed",
        });
      }

      if (hasNewDispatch) {
        const dispatchQty = Number(newDispatchQty);
        if (dispatchQty > order.totalPending) {
          setError(
            `Dispatch quantity cannot exceed pending quantity (${order.totalPending} ${order.quantityType})`,
          );
          return;
        }
        const pendingQty = Math.round((Number(order.totalPending) - dispatchQty) * 100) / 100;
        const transactionId = `trans${Date.now()}`;
        const transaction = {
          dispatch: dispatchQty,
          pending: pendingQty,
          timestamp: getIndianTimestamp(),
          shippingDetail:
            updatedOrders[order.orderId]?.shippingDetail ||
            order.shippingDetail ||
            "",
        };
        const dispatchRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/dispatchTransactions/${transactionId}`,
        );
        await set(dispatchRef, transaction);
        const existingBalanceRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/existingBalance`,
        );
        await set(existingBalanceRef, currentBalance);
        const statusRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/status`,
        );
        if (updatedData?.status) {
          await set(statusRef, updatedData.status);
          finalUpdateData.status = updatedData.status;
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        } else if (order.status !== "Dispatch") {
          await set(statusRef, "Dispatch");
          finalUpdateData.status = "Dispatch";
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        }
        if (pendingQty === 0) {
          const completedRef = ref(
            getDatabase(),
            `orders/${order.userId}/${order.orderId}/completed`,
          );
          await set(completedRef, true);
          await set(statusRef, "Completed");
          finalUpdateData.status = "Completed";
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Completed",
            `Your dispatch of ${dispatchQty} ${order.quantityType} for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } has been marked as the final dispatch. Your order is now complete. Thank you for ordering with us.`,
          );
        }
      }

      if (hasStatusChange || hasOtherChanges) {
        const orderRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}`,
        );
        await update(orderRef, finalUpdateData);
        if (hasStatusChange) {
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        }
        if (hasNewDispatch) {
          const existingBalanceRef = ref(
            getDatabase(),
            `orders/${order.userId}/${order.orderId}/existingBalance`,
          );
          await set(existingBalanceRef, currentBalance);
        }
        if (finalUpdateData.status === "Dispatch") {
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Dispatch",
            `Your order for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } has been dispatched and will be delivered soon.`,
          );
        } else if (finalUpdateData.status === "Underloading") {
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Underloading",
            `Your order for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } is currently under loading and will be dispatched shortly.`,
          );
        }
      }

      setEditingOrderId(null);
      setNewDispatchQty("");
      setShowLowBalanceWarning(false);
      setUpdatedOrders((prev) => {
        const newUpdatedOrders = { ...prev };
        delete newUpdatedOrders[order.orderId];
        return newUpdatedOrders;
      });

      const successMessage = document.createElement("div");
      successMessage.textContent = "Order updated successfully!";
      successMessage.style.position = "fixed";
      successMessage.style.top = "50%";
      successMessage.style.left = "50%";
      successMessage.style.transform = "translate(-50%, -50%)";
      successMessage.style.background = "#4CAF50";
      successMessage.style.color = "white";
      successMessage.style.padding = "15px 30px";
      successMessage.style.borderRadius = "10px";
      successMessage.style.boxShadow = "0 2px 10px rgba(0,0,0,0.3)";
      successMessage.style.zIndex = "9999";
      successMessage.style.fontSize = "18px";
      successMessage.style.fontWeight = "bold";
      successMessage.style.textAlign = "center";
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 2000);
      closeShippingModal();
      closeUpdateModal();
    } catch (err) {
      setError("Failed to update the order. Please try again.");
    } finally {
      const saveBtn = document.querySelector(".save-btn");
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = "Save Changes";
      }
    }
  };

  const hasPaymentOrPOFiles = (order) =>
    (order.paymentFileUrl && order.paymentFileUrl.trim() !== "") ||
    (order.poFileUrl && order.poFileUrl.trim() !== "") ||
    (order.utrNumber && order.utrNumber.trim() !== "");

  const shouldDisableFields = (status) =>
    status === "Pending" || status === "Underloading";

  if (isLoading)
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  if (error) return <div className="error-message">{error}</div>;

  const matchedExcelRows = getMatchedExcelRows();

  return (
    <div className="orders-container">
      <h1>
        Cement Orders
        <InfoIcon text="This section provides a complete overview of your received orders, including pending and dispatched details. You can view essential order information such as company name, brand, grade, quantity, and payment status. Additionally, you can update the Dispatch Quantity, Shipping Detail, and Dispatch Date to ensure accurate tracking of shipments. The shipping details include truck numbers and driver numbers for efficient logistics management." />
      </h1>

      <div className="orders-controls">
        <div className="control-item">
          <label htmlFor="search-input">Search</label>
          <input
            id="search-input"
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by company, site address, brand, order ID..."
          />
        </div>
        <div className="control-item">
          <label htmlFor="company-select">Company</label>
          <select
            id="company-select"
            value={selectedCompany}
            onChange={handleCompanyChange}
          >
            <option value="">All Companies</option>
            {companies.map((company, index) => (
              <option key={index} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>
        <div className="control-item">
          <label htmlFor="status-select">Status</label>
          <select
            id="status-select"
            value={selectedStatus}
            onChange={handleStatusChange}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Underloading">Underloading</option>
            <option value="Dispatch">Dispatch</option>
            <option value="Completed">Completed</option>
            <option value="Paused">Paused</option>
            <option value="Continued">Continued</option>
          </select>
        </div>
      </div>

      <div className="excel-import-section">
        <div className="excel-import-buttons-row">
          <button
            type="button"
            className="excel-import-button"
            onClick={() => setShowBalanceUpdate(true)}
          >
            <FaRupeeSign />
            <span>Balance update</span>
          </button>
          <button
            type="button"
            className="excel-import-button"
            onClick={() => setShowDispatchFiles(true)}
          >
            <FaFolderOpen />
            <span>Dispatch Files</span>
          </button>
          <button
            type="button"
            className="excel-import-button"
            onClick={() => setShowUnderloading(true)}
          >
            <FaTruckLoading />
            <span>Underloading</span>
          </button>
        </div>

        {showDispatchFiles && (
          <DispatchFiles
            onClose={() => setShowDispatchFiles(false)}
            orders={orders}
            onAutoUpdateOrder={handleDispatchAutoUpdate}
            onDispatchDataReady={handleDispatchDataReady}
          />
        )}
        {/* ===== MATCH MODE PANEL ===== */}
        {dispatchMatchMode && (
          <div className="dispatch-match-panel">
            <div className="dispatch-match-notice">
              Matched: {filteredOrders.length} order(s),{" "}
              {matchedExcelRows.length} Excel row(s).
              <button
                type="button"
                onClick={() => {
                  setDispatchMatchMode(false);
                  setDispatchRecords([]);
                  setSelectedOrderIds(new Set());
                  setSelectedExcelRowIndexes(new Set());
                }}
              >
                Show all orders
              </button>
            </div>

            {/* MATCHED EXCEL ROWS */}
            <div className="matched-excel-section">
              <h3>Matched Excel rows</h3>
              <p>
                Select the Excel row(s) whose dispatch data you want to apply.
              </p>
              {matchedExcelRows.length === 0 ? (
                <p>No Excel rows matched any order.</p>
              ) : (
                <div className="scrollable-table-container">
                  <table className="orders-table matched-excel-table">
                    <thead>
                      <tr className="table-header">
                        <th>
                          <input
                            type="checkbox"
                            checked={
                              matchedExcelRows.length > 0 &&
                              matchedExcelRows.every(({ index }) =>
                                selectedExcelRowIndexes.has(index),
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedExcelRowIndexes(
                                  new Set(
                                    matchedExcelRows.map(({ index }) => index),
                                  ),
                                );
                              } else {
                                setSelectedExcelRowIndexes(new Set());
                              }
                            }}
                          />
                        </th>
                        <th>Customer Code</th>
                        <th>Company</th>
                        <th>Grade</th>
                        <th>Qty</th>
                        <th>Invoice Amt</th>
                        <th>Truck No</th>
                        <th>Destination</th>
                        <th>Billing Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedExcelRows.map(({ record, index }) => (
                        <tr key={`excel-${index}`}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedExcelRowIndexes.has(index)}
                              onChange={(e) => {
                                setSelectedExcelRowIndexes((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(index);
                                  else next.delete(index);
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td>{record.customerCode}</td>
                          <td>{record.companyName}</td>
                          <td>{record.grade}</td>
                          <td>
                            {record.deliveredQty || record.dispatchQty || "—"}
                          </td>
                          <td>{record.invoiceAmt || "—"}</td>
                          <td>{record.truckNo || "—"}</td>
                          <td>{record.shippingAddress || "—"}</td>
                          <td>{record.dispatchDate || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* APPLY BUTTON */}
            <div className="dispatch-apply-bar" style={{ margin: "12px 0" }}>
              <button
                type="button"
                className="excel-import-button"
                onClick={applySelectedExcelToSelectedOrders}
                disabled={
                  selectedExcelRowIndexes.size === 0 ||
                  selectedOrderIds.size === 0
                }
              >
                Apply selected Excel → selected order(s)
              </button>
              <span style={{ marginLeft: 12, fontSize: 13, color: "#666" }}>
                {selectedExcelRowIndexes.size} Excel row(s),{" "}
                {selectedOrderIds.size} order(s) selected
              </span>
            </div>
          </div>
        )}
        {showUnderloading && (
          <div className="underloading-page">
            <UpdateUnderloading onClose={() => setShowUnderloading(false)} />
          </div>
        )}
      </div>
    {showBalanceUpdate && (
  <div
    className="balance-update-popup-overlay"
    onClick={(event) => {
      if (event.target === event.currentTarget) {
        setShowBalanceUpdate(false);
      }
    }}
  >
    <div className="balance-update-popup-content">
      <BalanceUpdatePage
        onClose={() => setShowBalanceUpdate(false)}
      />
    </div>
  </div>
)}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found for the selected filters.</p>
        </div>
      ) : (
        <div className="scrollable-table-container">
          <table className="orders-table">
            <thead>
              <tr className="table-header">
                {dispatchMatchMode && (
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        filteredOrders.length > 0 &&
                        filteredOrders.every((o) =>
                          selectedOrderIds.has(o.orderId),
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOrderIds(
                            new Set(filteredOrders.map((o) => o.orderId)),
                          );
                        } else {
                          setSelectedOrderIds(new Set());
                        }
                      }}
                    />
                  </th>
                )}
                <th>Order Dates</th>
                <th>Company Name</th>
                <th>Product Details</th>
                <th>Quantity</th>
                <th>Site Address</th>
                <th>Payment Amount</th>
                <th>Status</th>
                <th>Dispatch/Pending</th>
                <th>Current Balance</th>
                <th>Payment Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => {
                const isUpdated = order.status && order.status !== "Pending";
                const shouldHighlight = !isUpdated;
                const isPaused = isOrderPaused(order.orderId);
                const isContinued = isOrderContinued(order.orderId);
                let rowClassName = "";
                if (isPaused) rowClassName = "Paused-order";
                else if (isContinued) rowClassName = "Continued-order";
                else if (shouldHighlight) rowClassName = "highlight-flash";
                else rowClassName = index % 2 === 0 ? "even-row" : "odd-row";

                return (
                  <tr key={order.orderId} className={rowClassName}>
                    {dispatchMatchMode && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.has(order.orderId)}
                          onChange={(e) => {
                            setSelectedOrderIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(order.orderId);
                              else next.delete(order.orderId);
                              return next;
                            });
                          }}
                        />
                      </td>
                    )}
                    <td>
                      {new Date(order.timestamp).toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </td>
                    <td>{order.companyName}</td>
                    <td>
                      <div>
                        <strong>Brand:</strong> {order.brand}
                        <br />
                        <strong>Grade:</strong> {order.grade}
                        <br />
                        <strong>Type:</strong> {order.category || "Cement"}
                        <br />
                        <strong>Code:</strong> {order.code}
                      </div>
                    </td>
                    <td>
                      {order.quantity} {order.quantityType || "bags"}
                    </td>
                    <td>
                      {order.siteAddress ? (
                        <>
                          <div>{order.siteAddress.address}</div>
                          <div>
                            {order.siteAddress.state},{" "}
                            {order.siteAddress.pincode}
                          </div>
                          {order.siteAddress.transportation_type && (
                            <div style={{ marginTop: "6px" }}>
                              <strong>Transportation Type:</strong>{" "}
                              {order.siteAddress.transportation_type}
                            </div>
                          )}
                        </>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>{order.paymentAmount}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          isPaused
                            ? "status-Paused"
                            : order.status?.toLowerCase() || "pending"
                        }`}
                      >
                        {order.status || "Pending"} {isPaused && "(Paused)"}
                      </span>
                    </td>
                    <td>
                      <div className="dispatch-pending-info">
                        <div className="dispatch-info">
                          <span className="dispatch-label">Dispatch:</span>
                          <span className="dispatch-value">
                            {order.totalDispatched || 0}
                          </span>
                        </div>
                        <div className="pending-info">
                          <span className="pending-label">Pending:</span>
                          <span className="pending-value">
                            {order.totalPending}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="current-balance">
                        {order.currentBalance !== undefined
                          ? order.currentBalance
                          : "Loading..."}
                      </span>
                    </td>
                    <td>
                      {order.payment_status === "confirmed" ? (
                        <span className="status-badge status-confirmed">
                          Confirmed
                        </span>
                      ) : order.payment_status === "rejected" ? (
                        <span className="status-badge status-rejected">
                          Rejected
                        </span>
                      ) : (
                        <div className="payment-actions">
                          <button
                            className="btn-confirm"
                            onClick={() => confirmPayment(order)}
                            title="Confirm payment"
                          >
                            <FaCheckCircle />
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => rejectPayment(order)}
                            title="Reject payment"
                          >
                            <FaBan />
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons-container">
                        <button
                          className="edit-button"
                          onClick={() => openUpdateModal(order)}
                          disabled={
                            isPaused || order.payment_status !== "confirmed"
                          }
                          title={
                            order.payment_status !== "confirmed"
                              ? "Payment not confirmed"
                              : isPaused
                                ? "Order is paused"
                                : ""
                          }
                        >
                          <FaPencilAlt className="edit-icon" /> Update
                        </button>
                        {order.dispatchTransactions &&
                          Object.keys(order.dispatchTransactions).length >
                            0 && (
                            <button
                              className="view-transactions-button"
                              onClick={() => openTransactionsModal(order)}
                            >
                              <FaTruck className="transactions-icon" />{" "}
                              Dispatches
                            </button>
                          )}
                        {hasPaymentOrPOFiles(order) && (
                          <button
                            className="view-payment-po-button"
                            onClick={() => openPaymentPOModal(order)}
                          >
                            <FaInfoCircle className="payment-po-icon" />{" "}
                            Payment/PO & UTR
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Shipping Modal – unchanged from your original */}
      {showShippingModal && currentOrder && (
        <div className="modal-overlay">
          <div className="shipping-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Shipping Details</h3>
              <button className="close-modal-btn" onClick={closeShippingModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <p>
                  <strong>Company:</strong> {currentOrder.companyName}
                </p>
                <p>
                  <strong>Type:</strong> {currentOrder.category || "Cement"}
                </p>
                <p>
                  <strong>Brand:</strong> {currentOrder.brand}
                </p>
                <p>
                  <strong>Grade:</strong> {currentOrder.grade}
                </p>
                <p>
                  <strong>Quantity:</strong> {currentOrder.quantity}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
              </div>
              <div className="form-group">
                <label>Number of Trucks:</label>
                <select
                  value={truckCount}
                  onChange={handleTruckCountChange}
                  className="truck-count-select"
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                >
                  {[...Array(20)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Shipping Details:</label>
                <textarea
                  value={
                    updatedOrders[currentOrder.orderId]?.shippingDetail ||
                    currentOrder.shippingDetail ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      currentOrder.orderId,
                      "shippingDetail",
                      e.target.value,
                    )
                  }
                  className="shipping-textarea"
                  placeholder="Enter shipping details"
                  rows={4}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
              </div>
              <div className="modal-actions">
                <div className="action-buttons">
                  <div className="button-divider"></div>
                  <button
                    className="save-btn"
                    onClick={() => handleSave(currentOrder)}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Order Modal – unchanged from your original */}
      {showUpdateModal && currentOrder && (
        <div className="modal-overlay">
          <div className="shipping-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Order</h3>
              <button className="close-modal-btn" onClick={closeUpdateModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <p>
                  <strong>Company:</strong> {currentOrder.companyName}
                </p>
                <p>
                  <strong>Type:</strong> {currentOrder.category || "Cement"}
                </p>
                <p>
                  <strong>Brand:</strong> {currentOrder.brand}
                </p>
                <p>
                  <strong>Grade:</strong> {currentOrder.grade}
                </p>
                <p>
                  <strong>Quantity:</strong> {currentOrder.quantity}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Total Dispatched:</strong>{" "}
                  {currentOrder.totalDispatched || 0}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Pending:</strong> {currentOrder.totalPending}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Payment Amount:</strong>{" "}
                  {currentOrder.paymentAmount || 0}
                </p>
                <p>
                  <strong>Your Last Balance:</strong>{" "}
                  {currentOrder.existingBalance || 0}
                </p>
              </div>
              {currentOrder.dispatchTransactions &&
                Object.keys(currentOrder.dispatchTransactions).length > 0 && (
                  <div className="previous-transactions">
                    <h4>Previous Transactions</h4>
                    <div className="transactions-list">
                      {Object.entries(currentOrder.dispatchTransactions)
                        .sort(
                          ([, a], [, b]) =>
                            new Date(b.timestamp) - new Date(a.timestamp),
                        )
                        .map(([transId, transaction], index) => {
                          const transactionDate = new Date(
                            transaction.timestamp,
                          );
                          const formattedDate =
                            transactionDate.toLocaleDateString();
                          const formattedTime =
                            transactionDate.toLocaleTimeString();
                          return (
                            <div key={transId} className="transaction-item">
                              <div className="transaction-header">
                                <div className="transaction-title">
                                  <span className="transaction-id">
                                    {transId}
                                  </span>
                                  {index === 0 && (
                                    <span className="latest-badge">Latest</span>
                                  )}
                                </div>
                                <span className="transaction-date">
                                  {formattedDate} at {formattedTime}
                                </span>
                              </div>
                              <div className="transaction-details">
                                <div className="transaction-qty">
                                  <span className="qty-item">
                                    <span className="qty-label">
                                      Dispatched:
                                    </span>
                                    <span className="qty-value dispatch-value">
                                      {transaction.dispatch}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </span>
                                  <span className="qty-item">
                                    <span className="qty-label">Pending:</span>
                                    <span className="qty-value pending-value">
                                      {transaction.pending}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </span>
                                </div>
                                {transaction.shippingDetail && (
                                  <div className="transaction-shipping">
                                    <strong>Shipping:</strong>{" "}
                                    {transaction.shippingDetail}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              <div className="form-group">
                <label>Status:</label>
                <select
                  value={
                    updatedOrders[currentOrder.orderId]?.status ||
                    currentOrder.status ||
                    "Pending"
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange(currentOrder.orderId, "status", value);
                    if (value === "Delayed") setShowDelayPopup(true);
                  }}
                  className="status-select"
                >
                  <option value="Pending">Pending</option>
                  <option value="Delayed">Delayed</option>
                  <option value="Underloading">Underloading</option>
                  <option value="Dispatch">Dispatch</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  New Dispatch Quantity:{" "}
                  <span className="required-field">*</span>
                </label>
                <input
                  value={newDispatchQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (/^\d+$/.test(value) &&
                        Number.parseInt(value) <= currentOrder.totalPending)
                    ) {
                      setNewDispatchQty(value);
                      if (dispatchRate && value !== "") {
                        const deductionQty = getDeductionQuantityInBags(
                          value,
                          currentOrder?.quantityType,
                        );
                        const calculatedDeduction = Math.round(
                          Number.parseFloat(dispatchRate) * deductionQty,
                        );
                        setDeductionAmount(calculatedDeduction);
                      } else {
                        setDeductionAmount(0);
                      }
                    }
                  }}
                  className="dispatch-qty-input"
                  placeholder={`Enter dispatch quantity (max: ${currentOrder.totalPending})`}
                  onClick={(e) => e.target.select()}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
                <span className="quantity-type-label">
                  {currentOrder.quantityType || "bags"}
                </span>
              </div>
              <div className="balance-row">
                <div className="field-section">
                  <label>Rate:</label>
                  <input
                    value={dispatchRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        handleInputChange(currentOrder.orderId, "rate", value);
                      }
                    }}
                    className="dispatch-rate-input"
                    placeholder="Enter rate"
                    disabled={shouldDisableFields(
                      updatedOrders[currentOrder.orderId]?.status ||
                        currentOrder.status,
                    )}
                    type="number"
                    step="0.01"
                    min="0"
                    onWheel={(e) => e.preventDefault()}
                  />
                </div>
                <div className="field-section">
                  <label>Current Balance:</label>
                  <input
                    value={currentOrder.currentBalance || 0}
                    className="read-only-input"
                    placeholder="Current balance"
                    disabled={true}
                  />
                </div>
                <div className="field-section">
                  <label>Deduction Amount:</label>
                  <input
                    value={deductionAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setDeductionAmount(Number.parseFloat(value) || 0);
                      }
                    }}
                    className="deduction-amount-input"
                    placeholder="Deduction amount"
                    disabled={shouldDisableFields(
                      updatedOrders[currentOrder.orderId]?.status ||
                        currentOrder.status,
                    )}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Number of Trucks:</label>
                <select
                  value={truckCount}
                  onChange={handleTruckCountChange}
                  className="truck-count-select"
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                >
                  {[
                    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                    18, 19, 20,
                  ].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Shipping Details: <span className="required-field">*</span>
                </label>
                <textarea
                  value={
                    updatedOrders[currentOrder.orderId]?.shippingDetail ||
                    currentOrder.shippingDetail ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      currentOrder.orderId,
                      "shippingDetail",
                      e.target.value,
                    )
                  }
                  className="shipping-textarea"
                  placeholder="Enter shipping details"
                  rows={4}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
              </div>
              <div className="modal-actions">
                <div className="action-buttons">
                  <div className="button-divider"></div>
                  <button
                    className="save-btn"
                    onClick={() => handleSave(currentOrder)}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDelayPopup && (
        <div className="modal-overlay">
          <div className="delay-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Reason for Delay</h3>
            <textarea
              value={delayReason}
              onChange={(e) => setDelayReason(e.target.value)}
              placeholder="Enter delay reason"
              rows={4}
            />
            <div className="popup-actions">
              <button onClick={() => setShowDelayPopup(false)}>OK</button>
              <button
                onClick={() => {
                  setDelayReason("");
                  setShowDelayPopup(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions History Modal – unchanged */}
      {showTransactionsModal && currentOrder && (
        <div className="modal-overlay" onClick={closeTransactionsModal}>
          <div
            className="transactions-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Transaction History</h3>
              <button
                className="close-modal-btn"
                onClick={closeTransactionsModal}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <div className="order-header">
                  <h4>
                    {currentOrder.brand} {currentOrder.grade} -{" "}
                    {currentOrder.companyName}
                  </h4>
                  <span className="order-date">
                    Order Date: {currentOrder.timestamp}
                  </span>
                </div>
                <div className="order-details">
                  <div className="order-detail-item">
                    <span className="detail-label">Total Quantity:</span>
                    <span className="detail-value">
                      {currentOrder.quantity}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Total Dispatched:</span>
                    <span className="detail-value">
                      {currentOrder.totalDispatched || 0}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Pending:</span>
                    <span className="detail-value">
                      {currentOrder.totalPending}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Status:</span>
                    <span
                      className={`status-badge ${currentOrder.status?.toLowerCase() || "pending"}`}
                    >
                      {currentOrder.status || "Pending"}
                    </span>
                  </div>
                </div>
              </div>
              {currentOrder.dispatchTransactions &&
              Object.keys(currentOrder.dispatchTransactions).length > 0 ? (
                <div className="transactions-timeline">
                  {Object.entries(currentOrder.dispatchTransactions)
                    .sort(
                      ([, a], [, b]) =>
                        new Date(b.timestamp) - new Date(a.timestamp),
                    )
                    .map(([transId, transaction], index) => {
                      const transactionDate = new Date(transaction.timestamp);
                      const formattedDate =
                        transactionDate.toLocaleDateString();
                      const formattedTime =
                        transactionDate.toLocaleTimeString();
                      return (
                        <div key={transId} className="timeline-item">
                          <div className="timeline-marker">
                            <div className="timeline-icon">
                              <FaTruck />
                            </div>
                            <div className="timeline-line"></div>
                          </div>
                          <div className="timeline-content">
                            <div className="transaction-card">
                              <div className="transaction-header">
                                <div className="transaction-title">
                                  <span className="transaction-id">
                                    {transId}
                                  </span>
                                  <span
                                    className={
                                      index === 0 ? "latest-badge" : ""
                                    }
                                  >
                                    {index === 0 ? "Latest" : ""}
                                  </span>
                                </div>
                                <div className="transaction-date">
                                  <div className="date">{formattedDate}</div>
                                  <div className="time">{formattedTime}</div>
                                </div>
                              </div>
                              <div className="transaction-body">
                                <div className="transaction-quantities">
                                  <div className="quantity-item">
                                    <span className="quantity-label">
                                      Dispatched:
                                    </span>
                                    <span className="quantity-value dispatch-value">
                                      {transaction.dispatch}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </div>
                                  <div className="quantity-item">
                                    <span className="quantity-label">
                                      Pending:
                                    </span>
                                    <span className="quantity-value pending-value">
                                      {transaction.pending}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </div>
                                </div>
                                {transaction.shippingDetail && (
                                  <div className="shipping-details">
                                    <div className="shipping-header">
                                      Shipping Details:
                                    </div>
                                    <div className="shipping-trucks">
                                      {transaction.shippingDetail
                                        .split("|")
                                        .map((info, idx) => (
                                          <div key={idx} className="truck-info">
                                            <FaTruck className="truck-icon" />
                                            <span>{info.trim()}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="no-transactions">
                  <p>No transaction history available for this order.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment/PO Files Modal – unchanged */}
      {showPaymentPOModal && currentOrder && (
        <div className="modal-overlay" onClick={closePaymentPOModal}>
          <div
            className="payment-po-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Client Files</h3>
              <button className="close-modal-btn" onClick={closePaymentPOModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <div className="order-header">
                  <h4>
                    {currentOrder.brand} {currentOrder.grade} -{" "}
                    {currentOrder.companyName}
                  </h4>
                  <span className="order-date">
                    Order Date: {currentOrder.timestamp}
                  </span>
                </div>
              </div>
              <div className="files-container">
                <h4>Files</h4>
                {paymentPOFiles.payment && (
                  <div className="file-item">
                    <div className="file-label">Client Payment Proof</div>
                    <div className="file-actions">
                      <a
                        href={paymentPOFiles.payment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-action-btn"
                      >
                        <FaEye /> View
                      </a>
                    </div>
                  </div>
                )}
                {paymentPOFiles.po && (
                  <div className="file-item">
                    <div className="file-label">Client Purchase Order</div>
                    <div className="file-actions">
                      <a
                        href={paymentPOFiles.po}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-action-btn"
                      >
                        <FaEye /> View
                      </a>
                    </div>
                  </div>
                )}
                {paymentPOFiles.utrNumber && (
                  <div className="file-item">
                    <div className="file-label">UTR Number</div>
                    <div className="file-value">{paymentPOFiles.utrNumber}</div>
                  </div>
                )}
                {!paymentPOFiles.payment &&
                  !paymentPOFiles.po &&
                  !paymentPOFiles.utrNumber && (
                    <div className="no-files">
                      <p>No files available for this order.</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Low Balance Warning Modal – unchanged */}
      {showLowBalanceWarning && currentOrder && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div
            className="low-balance-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="warning-title">Insufficient Balance</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowLowBalanceWarning(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="warning-message">
                Client's current balance is not enough to continue dispatch
              </div>
              <div className="balance-details">
                <p>
                  <strong>Current Balance:</strong>{" "}
                  {currentOrder.currentBalance || 0}
                </p>
                <p>
                  <strong>Required Amount:</strong> {deductionAmount}
                </p>
                <p>
                  <strong>Shortage:</strong>{" "}
                  {Math.max(
                    0,
                    deductionAmount - (currentOrder.currentBalance || 0),
                  )}
                </p>
              </div>
              <div className="modal-actions">
                <button
                  className="notify-customer-btn"
                  onClick={() => {
                    sendLowBalanceNotification(
                      currentOrder.userId,
                      currentOrder.orderId,
                      currentOrder.currentBalance || 0,
                      deductionAmount,
                      currentOrder.brand,
                      currentOrder.grade,
                      currentOrder.siteAddress?.address || "your site",
                    );
                    setShowLowBalanceWarning(false);
                    const successMessage = document.createElement("div");
                    successMessage.className = "success-alert";
                    successMessage.textContent =
                      "Customer has been notified about low balance!";
                    document.body.appendChild(successMessage);
                    setTimeout(() => {
                      if (document.body.contains(successMessage)) {
                        document.body.removeChild(successMessage);
                      }
                    }, 2000);
                  }}
                >
                  Notify Customer
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => setShowLowBalanceWarning(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserOrders;
